/**
 * Cycle Execution — "How are our cycles running?"
 *
 * Cycle-level, so the segment control is off; the range selects cycles by
 * when they started, or one cycle. A bar is a cycle: clicking it opens the
 * cycle (the "relevant detail" of roadmap RP-7). The frame supplies export,
 * snapshot and schedule.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { DataTable, toneColor, type Column } from '../../../components/ds';
import { ReportFrame } from '../../../components/prioritization/reports/shared/ReportFrame';
import { ReportChartCard } from '../../../components/prioritization/reports/shared/ReportChartCard';
import { ReportStatCard } from '../../../components/prioritization/reports/shared/ReportStatCard';
import { ReportBreakdownChart, type BreakdownDatum } from '../../../components/prioritization/reports/shared/ReportBreakdownChart';
import { DrillLink } from '../../../components/prioritization/reports/shared/DrillLink';
import { cycleInRange, type RangeCycle } from '../../../components/prioritization/reports/shared/reportRange';
import { useReportControls, type ReportControls } from '../../../components/prioritization/reports/shared/useReportControls';
import type { ReportSnapshotSummary } from '../../../lib/reportSnapshotsService';
import { ReportPrintShell } from './ReportPrintShell';

const REPORT_KEY = 'cycle-execution';
const REPORT_PATH = '/prioritization/reports/execution';
const REPORT_NAME = 'Cycle Execution';
const QUESTION = 'How are our cycles running?';

interface CycleRow extends RangeCycle {
  status?: string;
  currentPhase?: string;
}

function cycleHref(id: string): string {
  return `/prioritization/cycles/${id}`;
}

function useCycleExecutionData(controls: ReportControls) {
  const cycles = useDegapProcessStore((s) => s.cycles) as unknown as CycleRow[];
  const sessions = useSessionStore((s) => s.sessions);
  const { resolved } = controls;

  return useMemo(() => {
    const inRange = cycles.filter((c) => cycleInRange(c, resolved));
    const ids = new Set(inRange.map((c) => c.id));
    const active = inRange.filter((c) => c.status === 'active').length;
    const completed = inRange.filter((c) => c.status === 'completed').length;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const scopedSessions = (sessions as any[]).filter((s) => (resolved.key === 'all' ? true : !!s.cycleId && ids.has(s.cycleId)));
    const completedSessions = scopedSessions.filter((s) => s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED').length;
    const publishedSessions = scopedSessions.filter((s) => s.state === 'PUBLISHED').length;
    const perCycle: Record<string, number> = {};
    for (const s of scopedSessions) if (s.cycleId) perCycle[s.cycleId] = (perCycle[s.cycleId] ?? 0) + 1;

    const tone: Record<string, string> = { active: toneColor('slate'), completed: toneColor('green'), paused: toneColor('mustard'), planned: toneColor('neutral') };
    const cycleData: BreakdownDatum[] = inRange.slice(0, 12).map((c) => ({
      key: c.id,
      label: c.name.length > 18 ? `${c.name.slice(0, 18)}…` : c.name,
      count: perCycle[c.id] ?? 0,
      color: tone[c.status ?? 'planned'] ?? toneColor('neutral'),
      href: cycleHref(c.id),
    }));

    const summary = (): ReportSnapshotSummary => ({
      stats: [
        { label: 'Cycles', value: inRange.length, sub: `${active} active` },
        { label: 'Completed', value: completed },
        { label: 'Sessions', value: scopedSessions.length, sub: `${completedSessions} completed` },
        { label: 'Published', value: publishedSessions },
      ],
      series: [{ title: 'Sessions per cycle', rows: cycleData.map((d) => ({ label: d.label, value: d.count, href: d.href, color: d.color })) }],
    });
    const csvRows = () => inRange.map((c) => ({ id: c.id, name: c.name, status: c.status ?? '', phase: c.currentPhase ?? '', sessions: perCycle[c.id] ?? 0 }));

    return { cycles: inRange, active, completed, totalSessions: scopedSessions.length, completedSessions, publishedSessions, cycleData, summary, csvRows };
  }, [cycles, sessions, resolved]);
}

export function CycleExecutionBody({ controls, print = false }: { controls: ReportControls; print?: boolean }) {
  const d = useCycleExecutionData(controls);

  const columns: Column<CycleRow>[] = [
    { key: 'name', header: 'Cycle', width: '50%', render: (c) => <Link to={cycleHref(c.id)} className="text-sm font-medium text-[var(--text-pri)] hover:text-[var(--text-ter)]">{c.name}</Link> },
    { key: 'status', header: 'Status', render: (c) => c.status ?? 'planned' },
    { key: 'phase', header: 'Phase', render: (c) => c.currentPhase ?? '—' },
    { key: 'open', header: '', align: 'right', render: (c) => (print ? null : <DrillLink to={cycleHref(c.id)} label="View" />) },
  ];

  return (
    <div className="space-y-6">
      <div className={`grid gap-4 ${print ? 'grid-cols-4' : 'grid-cols-2 md:grid-cols-4'}`}>
        <ReportStatCard label="Cycles" value={d.cycles.length} sub={`${d.active} active`} />
        <ReportStatCard label="Completed" value={d.completed} color="green" />
        <ReportStatCard label="Sessions" value={d.totalSessions} sub={`${d.completedSessions} completed`} />
        <ReportStatCard label="Published" value={d.publishedSessions} />
      </div>

      <ReportChartCard title="Sessions per cycle" description="Click a bar to open the cycle." print={print}>
        <ReportBreakdownChart data={d.cycleData} orientation="columns" valueLabel="Sessions" height={240} print={print} />
      </ReportChartCard>

      <DataTable<CycleRow>
        columns={columns}
        rows={d.cycles.slice(0, 30)}
        getRowKey={(c) => c.id}
        caption="Cycles in this range"
        density="compact"
        empty="No cycles in this range."
        footer={d.cycles.length > 30 ? `Showing the first 30 of ${d.cycles.length}. Export CSV for every row.` : undefined}
      />
    </div>
  );
}

export function CycleExecutionReport() {
  const controls = useReportControls();
  const d = useCycleExecutionData(controls);
  return (
    <ReportFrame
      reportKey={REPORT_KEY}
      reportPath={REPORT_PATH}
      name={REPORT_NAME}
      question={QUESTION}
      description="Cycles, their sessions, and what each has published. Every bar opens its cycle."
      controls={controls}
      showSegment={false}
      rangeNote="Windows count cycles by when they started."
      csv={{ filename: 'cycle-execution', rows: d.csvRows }}
      summary={d.summary}
    >
      <CycleExecutionBody controls={controls} />
    </ReportFrame>
  );
}

export function CycleExecutionPrintPage() {
  const controls = useReportControls();
  return (
    <ReportPrintShell
      title={REPORT_NAME}
      backHref={`${REPORT_PATH}${controls.search ? `?${controls.search}` : ''}`}
      meta={`${QUESTION} · ${controls.resolved.label} · ${new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
    >
      <CycleExecutionBody controls={controls} print />
    </ReportPrintShell>
  );
}
